import {AfterViewInit, ChangeDetectorRef, Component, inject, ViewChild, ViewEncapsulation} from '@angular/core';
import {MatDialogModule} from '@angular/material/dialog';
import {Observable, of, throwError} from 'rxjs';

import {TranslationsAdminComponent} from '../translations-admin.component';
import {
  CmsContentType,
  CmsPageSummary,
  ConnectionInfo,
  TranslationJob,
  TranslationJobRequest,
  TranslationSettings,
  TranslationSyncRunRequest,
  TranslationSyncResult,
  TranslationsApiService,
} from '../../../services/translations/translations-api.service';
import {ClientService} from '../../../services/auth/client.service';
import {ToastService} from '../../../services/layout/toast.service';
import {
  readTranslationsVisualFixtureState,
  TranslationsVisualFixtureState,
} from './translations-visual-fixture.guard';

class TranslationsVisualFixtureApi {
  constructor(private readonly fixtureState: TranslationsVisualFixtureState) {}

  readonly createJobCalls: TranslationJobRequest[] = [];
  readonly runSyncCalls: TranslationSyncRunRequest[] = [];

  listConnections(): Observable<ConnectionInfo[]> {
    return of([
      {
        id: 'fixture-hubspot',
        name: 'Fixture HubSpot portal',
        providerRef: 'fixture-portal',
        type: 'hubspot',
      },
    ]);
  }

  getSettings(): Observable<TranslationSettings> {
    return of(createFixtureSettings());
  }

  saveSettings(settings: TranslationSettings): Observable<TranslationSettings> {
    return of({...createFixtureSettings(), ...settings});
  }

  listPages(
    _contentType: CmsContentType,
    _options?: {
      connectionId?: string;
      connectionName?: string;
      after?: string;
      limit?: number;
      groupByMaster?: boolean;
    },
  ): Observable<{items: CmsPageSummary[]; next?: string | null}> {
    if (this.fixtureState === 'pages-error') {
      return throwError(() => new Error('fixture pages load error'));
    }

    return of({items: createFixturePages(), next: null});
  }

  listJobs(_limit = 50, _offset = 0): Observable<TranslationJob[]> {
    if (this.fixtureState === 'jobs-error') {
      return throwError(() => new Error('fixture jobs load error'));
    }

    return of(createFixtureJobs());
  }

  createJob(request: TranslationJobRequest): Observable<TranslationJob> {
    this.createJobCalls.push(request);
    return of({
      id: `fixture-job-${this.createJobCalls.length}`,
      clientId: 'fixture-client-translations',
      contentType: request.contentType,
      sourceId: request.sourceId,
      targetLanguage: request.targetLanguage,
      sourceLanguage: request.sourceLanguage,
      connectionId: request.connectionId,
      status: 'Queued',
      createdAt: '2026-07-02T10:00:00.000Z',
      updatedAt: '2026-07-02T10:00:00.000Z',
    });
  }

  runSync(request?: TranslationSyncRunRequest): Observable<TranslationSyncResult> {
    this.runSyncCalls.push(request ?? {});
    return of({pagesChecked: 3, pagesQueued: 2, jobsQueued: 2});
  }
}

@Component({
  selector: 'app-translations-visual-fixture',
  standalone: true,
  imports: [MatDialogModule, TranslationsAdminComponent],
  template: `
    <main class="translations-visual-fixture" [attr.data-fixture-state]="fixtureState">
      <app-translations-admin></app-translations-admin>
    </main>
  `,
  styles: [`
    .translations-visual-fixture {
      display: block;
      min-height: 100vh;
      background: #f6f8fb;
    }

    @media (max-width: 600px) {
      .translations-visual-fixture,
      .translations-visual-fixture .translations-page,
      .translations-visual-fixture mat-tab-group,
      .translations-visual-fixture .mat-mdc-tab-body-wrapper {
        box-sizing: border-box;
        max-width: 100vw;
        overflow-x: hidden;
        width: 100vw;
      }

      .translations-visual-fixture .setup-grid,
      .translations-visual-fixture .form-grid,
      .translations-visual-fixture .page-row {
        grid-template-columns: minmax(0, 1fr);
      }

      .translations-visual-fixture .job-card {
        align-items: stretch !important;
        display: flex !important;
        flex-direction: column !important;
        width: 100% !important;
      }

      .translations-visual-fixture .job-card > * {
        min-width: 0;
      }

      .translations-visual-fixture .mat-button-toggle-group {
        max-width: 100%;
      }

      .translations-visual-fixture .mat-button-toggle {
        min-width: 0;
      }

      .translations-visual-fixture .card-header-row,
      .translations-visual-fixture .job-header {
        align-items: flex-start;
        flex-direction: column;
      }

      .translations-visual-fixture .job-title,
      .translations-visual-fixture .warn,
      .translations-visual-fixture .warning-list {
        overflow-wrap: anywhere;
      }

      .translations-visual-fixture .warning-list {
        padding-left: 18px;
      }

      .translations-visual-fixture .warning-list li,
      .translations-visual-fixture .warn {
        max-width: 280px;
        white-space: normal;
      }
    }
  `],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: TranslationsApiService,
      useFactory: () => new TranslationsVisualFixtureApi(readFixtureState()),
    },
    {
      provide: ToastService,
      useValue: {
        showSuccess: () => undefined,
        showError: () => undefined,
        showInfo: () => undefined,
      },
    },
    {
      provide: ClientService,
      useValue: {
        client: {
          id: 'fixture-client-translations',
          name: 'Fixture Client',
          enabledProducts: ['Shine Translations'],
        },
        clients: [],
        getActiveProduct: () => 'Shine Translations',
        setActiveProduct: () => undefined,
      },
    },
  ],
})
export class TranslationsVisualFixtureComponent implements AfterViewInit {
  @ViewChild(TranslationsAdminComponent) private readonly translationsAdmin?: TranslationsAdminComponent;
  private readonly changeDetector = inject(ChangeDetectorRef);

  readonly fixtureState: TranslationsVisualFixtureState = readFixtureState();

  ngAfterViewInit(): void {
    const admin = this.translationsAdmin;
    if (!admin) return;

    admin.contentType = 'SitePage';
    admin.targetLanguage = 'nb';
    admin.selectedConnectionId = 'fixture-hubspot';

    if (this.fixtureState === 'jobs' || this.fixtureState === 'jobs-error') {
      admin.selectedTabIndex = 1;
    } else if (this.fixtureState === 'settings') {
      admin.selectedTabIndex = 2;
    } else {
      admin.selectedTabIndex = 0;
      admin.loadPages(true);
    }

    this.changeDetector.detectChanges();

    if (this.fixtureState === 'pages-confirmation') {
      const page = admin.pages[0];
      const variation = page?.shineVariations?.[0];
      if (page && variation) {
        admin.retranslateVariation(page, variation);
        this.changeDetector.detectChanges();
      }
    }
  }
}

function readFixtureState(): TranslationsVisualFixtureState {
  if (typeof window === 'undefined' || !window.location) {
    return 'pages-default';
  }

  return readTranslationsVisualFixtureState(window.location.href) ?? 'pages-default';
}

function createFixtureSettings(): TranslationSettings {
  return {
    clientId: 'fixture-client-translations',
    defaultInstructions: 'Preserve layout, links, HubL tokens, product names, and legal wording.',
    defaultModel: 'gpt-4.1-mini',
    formPolicy: 'auto',
    ctaPolicy: 'keep',
    autoTranslateLanguages: ['nb', 'de'],
    syncConnectionId: 'fixture-hubspot',
    syncSitePages: {
      mode: 'exclude',
      includeIds: [],
      excludeIds: [1003],
    },
    syncLandingPages: {
      mode: 'include',
      includeIds: [2001],
      excludeIds: [],
    },
    syncBlogPosts: {
      mode: 'off',
      includeIds: [],
      excludeIds: [],
    },
    updatedAt: '2026-07-02T09:30:00.000Z',
  };
}

function createFixturePages(): CmsPageSummary[] {
  return [
    {
      id: 1001,
      name: 'Pricing overview',
      language: 'en',
      slug: 'pricing',
      url: 'https://example.com/pricing',
      state: 'PUBLISHED',
      updatedAt: '2026-07-02T08:30:00.000Z',
      isMaster: true,
      variations: [
        {id: 1101, language: 'nb', state: 'DRAFT', updatedAt: '2026-07-01T11:00:00.000Z'},
        {id: 1102, language: 'de', state: 'PUBLISHED', updatedAt: '2026-06-30T10:00:00.000Z'},
      ],
      shineVariations: [
        {id: 1101, language: 'nb', state: 'DRAFT', updatedAt: '2026-07-01T11:00:00.000Z'},
        {id: 1102, language: 'de', state: 'PUBLISHED', updatedAt: '2026-06-30T10:00:00.000Z'},
      ],
      outOfSyncVariations: [
        {id: 1101, language: 'nb', state: 'DRAFT', updatedAt: '2026-07-01T11:00:00.000Z'},
      ],
    },
    {
      id: 1002,
      name: 'Customer launch checklist',
      language: 'en',
      slug: 'launch-checklist',
      url: 'https://example.com/launch-checklist',
      state: 'DRAFT',
      updatedAt: '2026-07-01T13:45:00.000Z',
      isMaster: true,
      variations: [],
      shineVariations: [],
      outOfSyncVariations: [],
    },
    {
      id: 1003,
      name: 'Legacy campaign archive',
      language: 'en',
      slug: 'legacy-campaign-archive',
      state: 'PUBLISHED',
      updatedAt: '2026-06-20T09:00:00.000Z',
      isMaster: true,
      variations: [],
      shineVariations: [],
      outOfSyncVariations: [],
    },
  ];
}

function createFixtureJobs(): TranslationJob[] {
  return [
    {
      id: 'fixture-job-failed',
      clientId: 'fixture-client-translations',
      contentType: 'SitePage',
      sourceId: 1001,
      targetLanguage: 'nb',
      sourceLanguage: 'en',
      connectionId: 'fixture-hubspot',
      status: 'Failed',
      createdAt: '2026-07-02T08:00:00.000Z',
      updatedAt: '2026-07-02T08:05:00.000Z',
      error: 'HubSpot rejected the draft update because the page was edited outside Shine.',
      logs: [
        {
          timestamp: '2026-07-02T08:01:00.000Z',
          level: 'warn',
          code: 'external_edit_detected',
          message: 'Norwegian Bokmal variation changed after the job started.',
        },
      ],
    },
    {
      id: 'fixture-job-running',
      clientId: 'fixture-client-translations',
      contentType: 'SitePage',
      sourceId: 1002,
      targetLanguage: 'de',
      sourceLanguage: 'en',
      connectionId: 'fixture-hubspot',
      status: 'Running',
      createdAt: '2026-07-02T09:15:00.000Z',
      updatedAt: '2026-07-02T09:20:00.000Z',
      logs: [
        {
          timestamp: '2026-07-02T09:16:00.000Z',
          level: 'info',
          code: 'draft_created',
          message: 'German draft variation created from the English source page.',
        },
      ],
    },
  ];
}
